import { useContext, useState } from "react";

import { ProjectRole, ProjectUser } from "@nens/rana/api-v1";

import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useProjectUserListQuery } from "@/queries/query-project-users";
import { useTenantUserListQuery } from "@/queries/query-tenant-user";
import { ProjectContext } from "@/stores/project-provider";

import { useAddUsersToProject } from "../api/add-project-user";
import { useInviteUserToProject } from "../api/invite-project-user";

export function useProjectUserInviteModal(onClose: () => void) {
  const project = useContext(ProjectContext);
  // Show a modal with all tenant users that are not already in the project.
  // User can filter the list by entering a search term.
  // Clicking on a user will add them to the project.
  // If the list is empty and the search term is a valid email address, offer to send
  // an invitation to that email address instead.

  const { page, offset, limit, setPage, setPageSize, searchString, setSearchString } =
    useRanaTableData({
      keyAddon: "projectUserInvite",
      initialPageSize: 50,
      initialSearchString: "",
    });

  // Get all tenant users who are not already in the project
  const tenantUsersQuery = useTenantUserListQuery({
    offset,
    limit,
    search: searchString.trim() || undefined,
  });
  const projectUsersQuery = useProjectUserListQuery(project.id);
  const tenantUsers = tenantUsersQuery?.data?.items ?? [];
  const projectUsers = projectUsersQuery?.data?.items ?? [];
  const projectUserIds = new Set(projectUsers.map((projectUser) => projectUser.user.id));
  const foundUsers = tenantUsers.filter((tenantUser) => !projectUserIds.has(tenantUser.id));

  const [showFinalStep, setShowFinalStep] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<ProjectUser[]>([]);

  const allUsersAreCollaborator = selectedUsers.every(
    (user) => user.role === ProjectRole.Collaborator
  );

  const updateUserRole = (userId: string, role: ProjectRole) => {
    setSelectedUsers((users) =>
      users.map((user) => (user.user.id === userId ? { ...user, role } : user))
    );
  };

  // If the search field is an email address, we can invite the user.
  const isValidEmail = (s: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  };
  const searchIsEmail = isValidEmail(searchString);
  const emailExists = tenantUsers.some(
    (user) => user.email.toLowerCase() === searchString.toLowerCase()
  );

  // Functions to add existing users to the project.
  const addUsersMutation = useAddUsersToProject(project.id);
  const addUsers = () => {
    addUsersMutation.mutate(selectedUsers, {
      onSuccess: onClose,
    });
  };
  // Functions to invite users that don't exist yet.
  const inviteUsersMutation = useInviteUserToProject();
  const inviteUser = () => {
    if (searchIsEmail && !emailExists) {
      inviteUsersMutation.mutate(searchString, {
        onSuccess: onClose,
      });
    }
  };

  const isLoading =
    tenantUsersQuery.isLoading ||
    projectUsersQuery.isLoading ||
    addUsersMutation.isMutating ||
    inviteUsersMutation.isMutating;

  const showInviteButton = searchIsEmail && !emailExists && !isLoading;
  const paginationProps = {
    page,
    numPages: Math.ceil((tenantUsersQuery.data?.total ?? 0) / limit),
    limit,
    setPageSize,
    setPage,
  };

  return {
    searchString,
    setSearchString,
    foundUsers,
    addUsers,
    showInviteButton,
    addUsersErrors: addUsersMutation.errors,
    inviteUsersErrors: inviteUsersMutation.errors,
    isLoading,
    inviteUser,
    showFinalStep,
    setShowFinalStep,
    selectedUsers,
    setSelectedUsers,
    updateUserRole,
    allUsersAreCollaborator,
    paginationProps,
  };
}
